'use client';

import { Chip } from '@common/components';
import { cn } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useOptimistic, useTransition } from 'react';

import { useCustomRouter } from '@/hooks/useCustomRouter';

import { ALL_THEME_ID, type MonthlyTheme } from '../_utils/festivalFetchers';

const CHIP_CLASS_NAME =
  'shrink-0 transition-[border-color,background-color,color,opacity] duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-B-500 active:opacity-80';

interface ThemeChipsProps {
  themes: MonthlyTheme[];
  activeThemeId?: string;
}

export const ThemeChips = ({ themes, activeThemeId }: ThemeChipsProps) => {
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [optimisticThemeId, setOptimisticThemeId] =
    useOptimistic(activeThemeId);

  const handleSelect = (themeId: string) => {
    if (themeId === optimisticThemeId) return;
    const params = new URLSearchParams(searchParams);
    params.set('theme', themeId);
    startTransition(() => {
      setOptimisticThemeId(themeId);
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    });
  };

  return (
    <div
      role="group"
      aria-label="축제 테마 필터"
      aria-busy={isPending}
      className={cn(
        'flex flex-wrap gap-6 px-24 transition-opacity duration-150',
        isPending && 'opacity-60',
      )}
    >
      <Chip
        key={ALL_THEME_ID}
        theme="sub"
        checked={optimisticThemeId === ALL_THEME_ID}
        className={CHIP_CLASS_NAME}
        onCheckedChange={() => handleSelect(ALL_THEME_ID)}
      >
        전체
      </Chip>
      {themes.map((theme) => {
        const themeId = theme.id;

        return themeId ? (
          <Chip
            key={themeId}
            theme="sub"
            checked={themeId === optimisticThemeId}
            className={CHIP_CLASS_NAME}
            onCheckedChange={() => handleSelect(themeId)}
          >
            {theme.name}
          </Chip>
        ) : null;
      })}
    </div>
  );
};
